import { useMemo, useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, X, ShieldAlert, Database, GitBranch, BookOpen,
  Ban, CheckCircle2, KeyRound, ArrowRight, AlertTriangle, Loader2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useReference } from "@/context/ReferenceContext";
import { PageHeader } from "@/components/CrudModule";
import { LEAD_FIELDS, MODULES } from "@/config/modules";
import { IMPORT_AS_OPTIONS, fieldsForImportTarget } from "@/config/importTargets.mjs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const SKIP = "__skip__";

const CLASS_META = {
  historical: { label: "Historical Data", icon: Database, cls: "bg-[#17324D]/10 text-[#17324D] ring-[#17324D]/20" },
  active_blueprint: { label: "Active / Live Model", icon: GitBranch, cls: "bg-[#315A7D]/10 text-[#2c5170] ring-[#315A7D]/25" },
  reference: { label: "Reference / Library", icon: BookOpen, cls: "bg-[#B28A3D]/12 text-[#7a5d27] ring-[#B28A3D]/30" },
  credentials: { label: "Sensitive Credentials", icon: ShieldAlert, cls: "bg-red-50 text-red-700 ring-red-200" },
  unknown_duplicate: { label: "Duplicate / Unknown", icon: Ban, cls: "bg-slate-100 text-slate-600 ring-slate-200" },
  empty: { label: "Empty / Template", icon: Ban, cls: "bg-slate-100 text-slate-600 ring-slate-200" },
};

function fieldsForTarget(target) {
  const leads = LEAD_FIELDS.map((f) => ({ key: f.key, label: f.label }));
  const policies = MODULES.policies.fields.map((f) => ({ key: f.key, label: f.label }))
    .concat([{ key: "annual_premium", label: "Annual Premium" }, { key: "issued_date", label: "Issued Date" },
             { key: "cancelled_date", label: "Cancelled Date" }, { key: "lead_source", label: "Lead Source" }]);
  return fieldsForImportTarget(target, leads, policies);
}

function Badge({ children, cls }) {
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}>{children}</span>;
}

export default function Import() {
  const fileRef = useRef();
  const { refresh } = useReference();
  const [fileName, setFileName] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [session, setSession] = useState(null);     // { session_id, tabs:[...] }
  const [decisions, setDecisions] = useState({});    // tabName -> { action, target, mapping, record_class, also_commissions }
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [credTab, setCredTab] = useState(null);      // tab object for migration
  const [users, setUsers] = useState([]);

  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setAnalyzing(true);
    setSession(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSession(data);
      const d = {};
      data.tabs.forEach((t) => {
        d[t.name] = {
          action: ["historical", "active_blueprint", "reference"].includes(t.classification) ? "import" : "skip",
          target: t.suggested_target === "credential_vault" ? "credential_vault" : t.suggested_target,
          mapping: { ...t.suggested_mapping },
          record_class: t.record_class || "Historical",
          also_commissions: t.classification === "historical",
        };
      });
      setDecisions(d);
      toast.success(`Analyzed ${data.tabs.length} tab(s) — review before committing`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not analyze file");
      setFileName("");
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    setSession(null); setDecisions({}); setResult(null); setFileName("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const setDec = (name, patch) => setDecisions((m) => ({ ...m, [name]: { ...m[name], ...patch } }));

  const importable = useMemo(
    () => (session?.tabs || []).filter((t) => decisions[t.name]?.action === "import" && !t.is_credential),
    [session, decisions]);

  const commit = async () => {
    setCommitting(true);
    try {
      const tabs = importable.map((t) => ({
        name: t.name, action: "import", target: decisions[t.name].target,
        mapping: decisions[t.name].mapping || {}, record_class: decisions[t.name].record_class,
        also_commissions: !!decisions[t.name].also_commissions,
      }));
      const { data } = await api.post("/import/commit", { session_id: session.session_id, tabs });
      setResult(data.results);
      refresh();
      toast.success("Approved data committed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div>
      <PageHeader title="Import & Review" subtitle="Upload a workbook, review the proposed mapping, then approve. Nothing is committed until you approve." />

      {/* Upload */}
      <div className="rounded-lg border border-border bg-card p-5 mb-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="overline mb-1">Step 1 · Upload Workbook</div>
            <p className="text-sm text-muted-foreground">Accepts .xlsx (all tabs) or .csv. The file is analyzed read-only — your source workbook is never changed.</p>
          </div>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onFile} className="hidden" data-testid="import-file" />
            <Button onClick={() => fileRef.current?.click()} disabled={analyzing} data-testid="import-upload-btn">
              {analyzing ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Analyzing…</> : <><Upload className="mr-1.5 h-4 w-4" /> Upload & Analyze</>}
            </Button>
            {fileName && !analyzing && (
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <FileSpreadsheet className="h-4 w-4" /> {fileName}
                <button onClick={reset} className="ml-1 text-muted-foreground hover:text-destructive" data-testid="import-reset"><X className="h-3.5 w-3.5" /></button>
              </span>
            )}
          </div>
        </div>
      </div>

      {session && (
        <>
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12px] text-amber-800 mb-4" data-testid="dryrun-banner">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span><strong>Dry-run.</strong> {session.tabs.length} tab(s) classified below. Credentials route to the secure vault migration — never imported as plain fields. Historical records are flagged and never become active leads or tasks.</span>
          </div>

          <div className="space-y-4">
            {session.tabs.map((t) => {
              const meta = CLASS_META[t.classification] || CLASS_META.unknown_duplicate;
              const Icon = meta.icon;
              const dec = decisions[t.name] || {};
              return (
                <div key={t.name} className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`tab-${t.name.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                  <div className="flex items-center justify-between gap-3 border-b border-border bg-secondary/40 px-4 py-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className="h-4 w-4 text-[#315A7D] shrink-0" />
                      <span className="font-heading font-semibold text-sm truncate">{t.name}</span>
                      <Badge cls={meta.cls}>{meta.label}</Badge>
                      <span className="text-xs text-muted-foreground">{t.real_rows} record(s)</span>
                      {t.duplicates > 0 && <Badge cls="bg-amber-50 text-amber-700 ring-amber-200">{t.duplicates} duplicate(s)</Badge>}
                      {t.missing_key_data > 0 && <Badge cls="bg-amber-50 text-amber-700 ring-amber-200">{t.missing_key_data} missing key data</Badge>}
                      {t.record_class && <Badge cls="bg-[#17324D]/10 text-[#17324D] ring-[#17324D]/20">{t.record_class}</Badge>}
                    </div>
                    {t.is_credential ? (
                      <Button size="sm" variant="outline" onClick={() => setCredTab(t)} data-testid={`migrate-${t.name.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                        <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Review for Vault Migration
                      </Button>
                    ) : t.real_rows > 0 ? (
                      <div className="flex items-center gap-2">
                        <Select value={dec.action} onValueChange={(v) => setDec(t.name, { action: v })}>
                          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`action-${t.name.replace(/[^a-zA-Z0-9]+/g, "-")}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="import">Import</SelectItem>
                            <SelectItem value="skip">Skip</SelectItem>
                          </SelectContent>
                        </Select>
                        {dec.action === "import" && (
                          <Select value={dec.target} onValueChange={(v) => setDec(t.name, { target: v })}>
                            <SelectTrigger className="h-8 w-[220px] text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{IMPORT_AS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      </div>
                    ) : (
                      <Badge cls="bg-slate-100 text-slate-500 ring-slate-200">No data to import</Badge>
                    )}
                  </div>

                  {/* Sensitive notice */}
                  {(t.sensitive_columns.length > 0 || t.excluded_columns.length > 0) && (
                    <div className="border-b border-border bg-red-50/50 px-4 py-2 text-[12px] text-red-700">
                      <ShieldAlert className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                      {t.sensitive_columns.length > 0 && <>Masked & vault-only: <strong>{t.sensitive_columns.join(", ")}</strong>. </>}
                      {t.excluded_columns.length > 0 && <>Excluded by policy (never stored): <strong>{t.excluded_columns.join(", ")}</strong>.</>}
                    </div>
                  )}

                  {/* Mapping (data tabs only) */}
                  {!t.is_credential && dec.action === "import" && ["leads", "policies", "followups", "book_recovery"].includes(dec.target) ? (
                    <div className="px-4 py-3 border-b border-border">
                      <div className="mb-2 flex items-center gap-2">
                        <div className="overline">Column Mapping</div>
                        {t.remembered_mapping && (
                          <Badge cls="bg-emerald-50 text-emerald-700 ring-emerald-200">Remembered from a previous import</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {fieldsForTarget(dec.target).map((f) => (
                          <div key={f.key} className="flex items-center gap-1.5">
                            <Label className="w-28 shrink-0 text-[11px] truncate" title={f.label}>{f.label}</Label>
                            <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                            <Select value={dec.mapping?.[f.key] || SKIP}
                              onValueChange={(v) => setDec(t.name, { mapping: { ...dec.mapping, [f.key]: v === SKIP ? undefined : v } })}>
                              <SelectTrigger className="h-7 text-[11px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value={SKIP}>— Skip —</SelectItem>
                                {t.headers.filter(Boolean).map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                      {dec.target === "policies" && (
                        <label className="mt-3 flex items-center gap-2 text-[12px] text-muted-foreground">
                          <Checkbox checked={!!dec.also_commissions} onCheckedChange={(v) => setDec(t.name, { also_commissions: !!v })} data-testid={`comm-${t.name.replace(/[^a-zA-Z0-9]+/g, "-")}`} />
                          Also create historical Commission records from commission columns
                        </label>
                      )}
                    </div>
                  ) : null}

                  {/* Database effect preview (FollowUps only) — what Approve will actually do,
                      before anything is committed */}
                  {!t.is_credential && dec.action === "import" && dec.target === "followups" && t.import_preview && (
                    <div className="px-4 py-3 border-b border-border bg-[#315A7D]/5" data-testid={`followup-preview-${t.name.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                      <div className="overline mb-2">Database Effect if You Approve</div>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge cls="bg-slate-100 text-slate-700 ring-slate-200">{t.import_preview.clients_matched} existing client(s) matched</Badge>
                        <Badge cls="bg-emerald-50 text-emerald-700 ring-emerald-200">{t.import_preview.clients_new} new client(s)</Badge>
                        <Badge cls="bg-slate-100 text-slate-700 ring-slate-200">{t.import_preview.policies_matched} existing policy(ies) matched/updated</Badge>
                        <Badge cls="bg-emerald-50 text-emerald-700 ring-emerald-200">{t.import_preview.policies_new} new policy(ies)</Badge>
                        <Badge cls="bg-[#315A7D]/10 text-[#2c5170] ring-[#315A7D]/25">{t.import_preview.followups_to_update} follow-up(s) to update</Badge>
                        <Badge cls="bg-emerald-50 text-emerald-700 ring-emerald-200">{t.import_preview.followups_to_create} new follow-up(s)</Badge>
                        {t.import_preview.followups_ambiguous > 0 && (
                          <Badge cls="bg-amber-50 text-amber-700 ring-amber-200">{t.import_preview.followups_ambiguous} ambiguous / needs review</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sample preview */}
                  {t.sample?.length > 0 && (
                    <div className="px-4 py-3 overflow-x-auto">
                      <div className="overline mb-2">Sample {t.is_credential ? "(masked)" : "(first rows)"}</div>
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            {Object.keys(t.sample[0]).slice(0, 8).map((h, hi) => <TableHead key={`${h}-${hi}`} className="overline whitespace-nowrap">{h}</TableHead>)}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {t.sample.slice(0, 4).map((row, i) => (
                            <TableRow key={i} className="text-[12px]">
                              {Object.keys(t.sample[0]).slice(0, 8).map((h, hi) => <TableCell key={`${h}-${hi}`} className="whitespace-nowrap max-w-[180px] truncate">{row[h] || <span className="text-muted-foreground">—</span>}</TableCell>)}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Commit bar */}
          <div className="sticky bottom-0 mt-5 flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3 shadow-lg">
            <div className="text-sm text-muted-foreground">
              <strong className="text-foreground">{importable.length}</strong> tab(s) selected to import ·
              {" "}{importable.reduce((s, t) => s + t.real_rows, 0)} record(s). Credentials handled separately via vault migration.
            </div>
            <Button onClick={commit} disabled={committing || importable.length === 0} data-testid="commit-btn">
              {committing ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Committing…</> : <><CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve & Commit Selected</>}
            </Button>
          </div>
        </>
      )}

      {result && (
        <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="import-result">
          <div className="font-semibold mb-1 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Import complete</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {result.map((r, i) => (
              <li key={i}>{r.skipped ? `${r.name}: skipped` : [
                `${r.name} → ${r.target}: ${r.inserted} record(s)`,
                r.updated ? `${r.updated} updated` : "",
                r.clients_linked ? `${r.clients_linked} linked` : "",
                r.clients_created ? `${r.clients_created} client(s) created` : "",
                r.pending_review ? `${r.pending_review} pending link review` : "",
                r.duplicates ? `${r.duplicates} duplicate(s) skipped` : "",
                r.followups_ambiguous ? `${r.followups_ambiguous} ambiguous — needs manual review, not touched` : "",
                r.commissions ? `${r.commissions} commission(s)` : "",
              ].filter(Boolean).join(" · ")}</li>
            ))}
          </ul>
        </div>
      )}

      {credTab && (
        <CredentialMigration tab={credTab} sessionId={session.session_id} users={users} onClose={() => setCredTab(null)} />
      )}
    </div>
  );
}

const SCOPES = [
  { value: "carrier", label: "Carrier" }, { value: "agency", label: "Agency" },
  { value: "quote_tool", label: "Quote Tool" }, { value: "crm", label: "CRM" },
  { value: "lead_vendor", label: "Lead Vendor" }, { value: "licensing", label: "Licensing" },
];
const STATUSES = [
  { value: "pending_verification", label: "Pending Verification" }, { value: "active", label: "Active" },
  { value: "expired", label: "Expired" }, { value: "disabled", label: "Disabled" },
];

function CredentialMigration({ tab, sessionId, users, onClose }) {
  const [rows, setRows] = useState(() => (tab.sample || []).map((s, idx) => ({
    row_index: idx, include: true, owner_user_id: "", owner_name: "",
    scope_type: tab.name.toUpperCase().includes("STATE") ? "licensing" : "carrier",
    connects_to: "", label: tab.name, allowed_roles: ["admin", "ops_manager"], status: "pending_verification",
    _preview: s,
  })));
  const [saving, setSaving] = useState(false);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        session_id: sessionId, tab: tab.name,
        rows: rows.map((r) => ({
          row_index: r.row_index, include: r.include, owner_user_id: r.owner_user_id,
          owner_name: users.find((u) => u.id === r.owner_user_id)?.name || "",
          scope_type: r.scope_type, connects_to: r.connects_to, label: r.label,
          allowed_roles: r.allowed_roles, status: r.status,
        })),
      };
      const { data } = await api.post("/credentials/migrate", payload);
      toast.success(`Migrated ${data.inserted} credential(s) to the secure vault`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Migration failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cred-migration">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-5xl max-h-[88vh] overflow-hidden rounded-xl bg-card shadow-2xl flex flex-col">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="font-heading text-lg font-bold flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#B28A3D]" /> Credential Vault Migration · {tab.name}</div>
            <p className="text-xs text-muted-foreground mt-0.5">Secrets are masked here and encrypted on save. Recovery codes / OTP / MFA / bank / card / SSN are never stored.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-auto p-5 space-y-3">
          {rows.map((r, i) => (
            <div key={i} className={`rounded-lg border p-3 ${r.include ? "border-border bg-secondary/30" : "border-dashed border-border opacity-60"}`} data-testid={`cred-row-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <Checkbox checked={r.include} onCheckedChange={(v) => setRow(i, { include: !!v })} data-testid={`cred-include-${i}`} />
                <span className="text-xs text-muted-foreground">Include this credential</span>
                <span className="ml-auto text-[11px] font-mono text-muted-foreground truncate max-w-[40%]">
                  {Object.entries(r._preview).slice(0, 4).map(([k, v]) => `${k}: ${v}`).join("  ·  ")}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <div><Label className="text-[10px]">Label</Label><Input className="h-8 text-xs" value={r.label} onChange={(e) => setRow(i, { label: e.target.value })} /></div>
                <div><Label className="text-[10px]">Connects To</Label><Input className="h-8 text-xs" value={r.connects_to} onChange={(e) => setRow(i, { connects_to: e.target.value })} placeholder="Carrier/vendor" /></div>
                <div>
                  <Label className="text-[10px]">Scope</Label>
                  <Select value={r.scope_type} onValueChange={(v) => setRow(i, { scope_type: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Owner</Label>
                  <Select value={r.owner_user_id || "__none__"} onValueChange={(v) => setRow(i, { owner_user_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Agency" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Agency (shared)</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Status</Label>
                  <Select value={r.status} onValueChange={(v) => setRow(i, { status: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Agent can use?</Label>
                  <label className="flex h-8 items-center gap-1.5 text-[11px]">
                    <Checkbox checked={r.allowed_roles.includes("agent")}
                      onCheckedChange={(v) => setRow(i, { allowed_roles: v ? ["admin", "ops_manager", "agent"] : ["admin", "ops_manager"] })} />
                    Allow assigned agents
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-border px-5 py-3">
          <span className="text-xs text-muted-foreground">{rows.filter((r) => r.include).length} of {rows.length} will be migrated to the encrypted vault.</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="cred-migrate-save">
              {saving ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Migrating…</> : <>Migrate to Vault</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
